import type { FC, MouseEvent, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { onFrame, scrollToTarget } from '@/lib/motion/engine'
import { detectScheme } from '@/lib/motion/scheme'

const SEGMENTS = 10

/**
 * A folding ruler on the right edge that reads the scroll position in metres.
 * A standard Norwegian tommestokk is two metres long, folded in ten segments.
 */
export const RulerProgress: FC = (): ReactNode => {
    const root = useRef<HTMLDivElement>(null)
    const marker = useRef<HTMLDivElement>(null)
    const reading = useRef<HTMLSpanElement>(null)

    useEffect(() => {
        const el = root.current

        if (!el) return

        let lastText = ''
        let lastScheme = ''

        return onFrame((scrollY, vh) => {
            const max = document.documentElement.scrollHeight - vh
            const progress = max > 0 ? Math.min(1, Math.max(0, scrollY / max)) : 0

            if (marker.current) marker.current.style.transform = `translate3d(0, ${(progress * 100).toFixed(3)}%, 0)`

            const metres = (progress * 2).toFixed(2).replace('.', ',')
            const textValue = `${metres} m`

            if (textValue !== lastText && reading.current) {
                lastText = textValue
                reading.current.textContent = textValue
            }

            const rect = el.getBoundingClientRect()
            const scheme = detectScheme(rect.left - 24, vh / 2, el)

            if (scheme !== lastScheme) {
                lastScheme = scheme
                el.dataset.scheme = scheme
            }
        })
    }, [])

    const jump = (event: MouseEvent<HTMLDivElement>): void => {
        const rect = event.currentTarget.getBoundingClientRect()
        const fraction = Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height))
        const max = document.documentElement.scrollHeight - window.innerHeight

        scrollToTarget(fraction * max, 0)
    }

    return (
        <div ref={root} aria-hidden='true' className='ruler' data-scheme='dark'>
            <div className='ruler-body' onClick={jump}>
                {Array.from({ length: SEGMENTS }).map((_, index) => (
                    <div key={index} className='ruler-segment'>
                        <span className='ruler-number'>{(index + 1) * 20}</span>
                    </div>
                ))}

                <div className='ruler-track'>
                    <div ref={marker} className='ruler-marker'>
                        <span className='ruler-reading'>
                            <span ref={reading}>0,00 m</span>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    )
}
